import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ShieldCheck,
  LockKey,
  Certificate,
  CloudCheck,
  Eye,
  Bell,
} from "@phosphor-icons/react";

const PRACTICES = [
  {
    icon: LockKey,
    title: "Encryption Everywhere",
    description:
      "All data is encrypted in transit with TLS 1.3 and at rest with AES-256. API keys and secrets are stored in dedicated vaults with automatic rotation.",
  },
  {
    icon: Certificate,
    title: "Built to Recognised Standards",
    description:
      "We design our controls around frameworks like SOC 2 and ISO 27001 as we mature toward formal certification. We will publish attestations here once they are issued.",
  },
  {
    icon: CloudCheck,
    title: "Secure Infrastructure",
    description:
      "Hosted on AWS with VPC isolation, automated patching, and geo-redundant backups. All production systems run in hardened containers with minimal attack surface.",
  },
  {
    icon: Eye,
    title: "Access Controls",
    description:
      "Role-based access control (RBAC) with granular permissions. Multi-factor authentication enforced for all administrative access. SSO support via SAML 2.0 and OIDC.",
  },
  {
    icon: Bell,
    title: "Monitoring & Incident Response",
    description:
      "Automated monitoring with real-time alerting on production systems. Documented incident response procedures and post-incident reviews on every material event.",
  },
  {
    icon: ShieldCheck,
    title: "Ongoing Security Testing",
    description:
      "Continuous dependency and vulnerability scanning, internal security reviews on changes, and a responsible disclosure channel for security researchers.",
  },
];

export function SecurityPage() {
  return (
    <main className="pt-28 pb-20">
      <div className="mx-auto max-w-4xl px-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        <div className="mt-8 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-500/20 bg-primary-500/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary-500 dark:border-primary-400/20 dark:bg-primary-400/5 dark:text-primary-300">
            Security
          </span>
          <h1 className="mt-6 text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
            Your data security is our{" "}
            <span className="gradient-text">top priority</span>
          </h1>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
            Opero is built with security in mind from the ground up. We follow
            recognised industry practices and are working toward formal
            third-party attestation as the platform matures.
          </p>
        </div>

        <div className="mt-16 grid gap-6 sm:grid-cols-2">
          {PRACTICES.map((practice) => (
            <div
              key={practice.title}
              className="rounded-2xl border border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-900 p-7 transition-all duration-300 hover:shadow-lg"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500/10 to-primary-400/10 dark:from-primary-500/20 dark:to-primary-400/20">
                <practice.icon
                  size={22}
                  weight="duotone"
                  className="text-primary-500 dark:text-primary-400"
                />
              </div>
              <h3 className="mt-4 text-base font-semibold text-slate-900 dark:text-white">
                {practice.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                {practice.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-16 rounded-2xl border border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-900 p-8 text-center">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            Found a vulnerability?
          </h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 max-w-md mx-auto">
            We welcome responsible disclosure from security researchers. Please
            report vulnerabilities to our security team.
          </p>
          <a
            href="mailto:corporate@digiits.com"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-primary-500/25 transition-all duration-200 hover:shadow-xl hover:shadow-primary-500/30"
          >
            Report a Vulnerability
          </a>
        </div>
      </div>
    </main>
  );
}
